import type { FieldExplainerContent } from './types';

export type MortgageExplainerContext = {
  input: {
    homePrice: number;
    downPayment: number;
    interestRate: number;
    loanTermYears: number;
    propertyTax: number;
    homeInsurance: number;
    pmi: number;
    hoa: number;
    extraPayment: number;
  };
  result: {
    loanAmount: number;
    exactDownPaymentRatio: number;
    monthlyPI: number;
    monthlyTaxInsurance: number;
    totalMonthlyPayment: number;
    numberOfPayments: number;
    totalInterest: number;
    totalInterestWithoutExtra: number;
    interestSaved: number;
    actualLoanTerm: number;
    payoffYear: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function getMortgageFieldExplainers(ctx: MortgageExplainerContext): FieldExplainerContent[] {
  const { homePrice, downPayment, interestRate, loanTermYears, propertyTax, homeInsurance, pmi, hoa, extraPayment } = ctx.input;
  const {
    loanAmount,
    exactDownPaymentRatio,
    monthlyPI,
    monthlyTaxInsurance,
    totalMonthlyPayment,
    numberOfPayments,
    totalInterest,
    totalInterestWithoutExtra,
    interestSaved,
    actualLoanTerm,
    payoffYear
  } = ctx.result;

  const monthlyRate = interestRate / 100 / 12;
  // The unrounded ratio decides PMI in the engine; showing the rounded figure
  // here used to claim "$58,500 / $300,000 = 20%" and a waiver at 19.5% down.
  const pmiApplies = exactDownPaymentRatio < 20;
  const downRatioDisplay = `${exactDownPaymentRatio.toFixed(2).replace(/\.?0+$/, '')}%`;
  const currentYear = payoffYear - actualLoanTerm;
  const monthlyTax = propertyTax / 12;
  const monthlyInsurance = homeInsurance / 12;

  return [
    {
      id: 'homePrice',
      kind: 'input',
      label: 'Home price',
      plain: 'The purchase price. Subtracting your down payment from it is the only thing this field does — the difference is the loan amount every other calculation on this page is built from.',
      formula: `loan amount = ${usd.format(homePrice)} − ${usd.format(downPayment)}\n           = ${usd.format(loanAmount)}`,
      feeds: 'The loan amount, which drives the monthly payment, the amortization schedule, and every equity-percent figure in the table.'
    },
    {
      id: 'downPayment',
      kind: 'input',
      label: 'Down payment',
      plain: 'Cash paid upfront that reduces the loan balance directly. It also decides whether PMI gets added to your monthly payment: this calculator waives it once your down payment reaches 20% of the price.',
      formula: `${usd.format(downPayment)} / ${usd.format(homePrice)} = ${downRatioDisplay} down\nPMI ${pmiApplies ? 'applied' : 'waived'} (${downRatioDisplay} ${pmiApplies ? '<' : '≥'} 20%)`,
      feeds: 'The loan amount, the down-payment percentage, and whether the PMI field below is added to your monthly payment.'
    },
    {
      id: 'interestRate',
      kind: 'input',
      label: 'Interest rate',
      plain: 'The annual rate, converted to a monthly rate and compounded over every payment with the standard fixed-rate amortization formula that solves for a level payment.',
      formula:
        interestRate > 0
          ? `r = ${interestRate}% / 12 = ${monthlyRate.toFixed(6)} per month\npayment = L·r(1+r)ⁿ / ((1+r)ⁿ − 1)`
          : `r = 0%, so the amortization formula's (1+r)ⁿ − 1 denominator\nis zero — payment falls back to L / n instead`,
      feeds: 'Monthly principal & interest, and the interest column of every row in the amortization schedule.',
      assumption:
        interestRate === 0
          ? 'At exactly 0%, the standard formula divides by zero, so this calculator switches to a flat loan-amount-over-payments split: every payment is pure principal and total interest is $0.'
          : 'One flat rate for the full term — no adjustable-rate resets and no refinancing modeled.'
    },
    {
      id: 'loanTermYears',
      kind: 'input',
      label: 'Loan term (years)',
      plain: 'How many years the fixed payment is spread across. A longer term lowers the monthly payment but raises total interest, since the same balance compounds over more payments.',
      formula: `n = ${loanTermYears} × 12 = ${numberOfPayments} monthly payments`,
      feeds: 'The exponent in the monthly payment formula and the length of the baseline (no-extra-payment) amortization schedule.'
    },
    {
      id: 'propertyTax',
      kind: 'input',
      label: 'Annual property tax',
      plain: 'An annual estimate, divided evenly by 12 and folded into your total monthly housing payment. It never touches the loan balance or the interest math.',
      formula: `${usd.format(propertyTax)} / 12 = ${usdCents.format(monthlyTax)} per month`,
      feeds: 'Total monthly housing payment only.'
    },
    {
      id: 'homeInsurance',
      kind: 'input',
      label: 'Annual home insurance',
      plain: 'Same treatment as property tax: an annual figure divided by 12 and added to the monthly housing payment, with no effect on the loan itself.',
      formula: `${usd.format(homeInsurance)} / 12 = ${usdCents.format(monthlyInsurance)} per month`,
      feeds: 'Total monthly housing payment only.'
    },
    {
      id: 'pmi',
      kind: 'input',
      label: 'Monthly PMI',
      plain: 'Private mortgage insurance, added to your monthly payment only while your down payment is under 20% of the home price.',
      formula: pmiApplies
        ? `${usd.format(pmi)} added — down payment is ${downRatioDisplay} (< 20%)`
        : `not added — down payment is ${downRatioDisplay} (≥ 20%), so this field is ignored`,
      feeds: 'Total monthly housing payment, but only when the down-payment threshold above is not met.',
      assumption:
        'This is the flat dollar amount you enter, held constant for the life of the loan wherever it applies. Real PMI is usually a percentage of the balance and is dropped automatically once equity crosses roughly 20% — this model does not remove it as the balance amortizes down.'
    },
    {
      id: 'hoa',
      kind: 'input',
      label: 'Monthly HOA',
      plain: 'Added dollar-for-dollar to your monthly housing payment, unrelated to the loan itself.',
      formula: `${usd.format(hoa)} added directly to the monthly payment`,
      feeds: 'Total monthly housing payment only.',
      assumption: 'Held flat for the entire term — no annual HOA increases are modeled.'
    },
    {
      id: 'extraPayment',
      kind: 'input',
      label: 'Extra monthly principal',
      plain: 'Extra cash applied to principal every month, on top of the regular payment, before interest is calculated on the following month\'s smaller balance. A second amortization schedule is run with this folded in so you can see the effect.',
      formula:
        extraPayment > 0
          ? `each month: principal = (P&I − that month's interest) + ${usd.format(extraPayment)}\npayoff: ${actualLoanTerm} yrs (vs ${loanTermYears} yrs without it)\ninterest saved: ${usd.format(interestSaved)}`
          : `${usd.format(0)} extra — the baseline ${loanTermYears}-year schedule is used everywhere on this page`,
      feeds: 'The "with extra payment" amortization schedule, the total interest figure, and the payoff year — whenever it is greater than zero.',
      assumption:
        extraPayment === 0
          ? 'At $0, the extra-payment schedule is never built; every card and chart on this page reads from the standard schedule instead.'
          : undefined
    },
    {
      id: 'monthlyPI',
      kind: 'result',
      label: 'Monthly principal + interest',
      plain: 'The fixed payment that pays off the loan amount in exactly the term you set, before any extra payments — the core amortized payment lenders quote.',
      formula:
        interestRate > 0
          ? `L = ${usd.format(loanAmount)}, r = ${monthlyRate.toFixed(6)}, n = ${numberOfPayments}\npayment = L·r(1+r)ⁿ / ((1+r)ⁿ − 1)\n        = ${usdCents.format(monthlyPI)}`
          : `L = ${usd.format(loanAmount)}, n = ${numberOfPayments}, r = 0%\npayment = L / n = ${usdCents.format(monthlyPI)}`,
      feeds: 'Total monthly housing payment, and the baseline amortization schedule used whenever there is no extra payment.'
    },
    {
      id: 'totalMonthlyPayment',
      kind: 'result',
      label: 'Total monthly housing payment',
      plain:
        extraPayment > 0
          ? 'The required payment each month: principal & interest plus taxes, insurance, PMI (if it applies), and HOA. Your extra principal payment comes on top of this figure — the payoff and interest results assume you send both.'
          : 'What you would actually send each month: principal & interest plus taxes, insurance, PMI (if it applies), and HOA, all added together.',
      formula:
        extraPayment > 0
          ? `${usdCents.format(monthlyPI)} P&I + ${usdCents.format(monthlyTaxInsurance)} taxes/insurance/PMI/HOA\n= ${usdCents.format(totalMonthlyPayment)} required\n+ ${usdCents.format(extraPayment)} extra principal\n= ${usdCents.format(totalMonthlyPayment + extraPayment)} actually sent`
          : `${usdCents.format(monthlyPI)} P&I + ${usdCents.format(monthlyTaxInsurance)} taxes/insurance/PMI/HOA\n= ${usdCents.format(totalMonthlyPayment)}`,
      feeds: 'Nothing further downstream — it is the top-line payment figure for the page.'
    },
    {
      id: 'totalInterest',
      kind: 'result',
      label: 'Total interest',
      plain: extraPayment > 0
        ? 'Total interest across the life of the loan using the extra-payment schedule — summed year by year from the actual amortization run, not estimated from a formula.'
        : 'Total interest across the life of the loan: the sum of every payment made, minus the loan amount itself.',
      formula:
        extraPayment > 0
          ? `sum of each year's interest in the extra-payment schedule\n= ${usd.format(totalInterest)}\n(vs ${usd.format(totalInterestWithoutExtra)} without the extra payment)`
          : `(${usdCents.format(monthlyPI)} × ${numberOfPayments}) − ${usd.format(loanAmount)}\n= ${usd.format(totalInterest)}`,
      feeds: 'The "saved with extra payment" note beneath this card and the interest line on the amortization chart.'
    },
    {
      id: 'payoffYear',
      kind: 'result',
      label: 'Projected payoff year',
      plain: `The calendar year the active schedule reaches a zero balance — ${extraPayment > 0 ? "the extra-payment schedule's length" : 'your full loan term'}, added to the year you started.`,
      formula: `${currentYear} + ${actualLoanTerm} years = ${payoffYear}`,
      feeds: 'Nothing further downstream; it is a check figure alongside the estimated term.',
      assumption: 'Counted in whole calendar years: a payoff that lands mid-year still counts as a full final year, so this can read up to about eleven months later than a strict payment-by-payment count.'
    }
  ];
}
